// Copy extracted from the supplied additions.json and app.js/gold.js templates.
// This is page content only; routing, SEO registration and the connected form remain app-owned.
export const voicesSolutionsData = {
  "/ai-agents": {
    "path": "/ai-agents",
    "type": "agents",
    "name": "Custom AI Agents",
    "category": "Custom AI agents",
    "headline": "AI built around you.",
    "description": "We design agents around the work your business needs to do. Grounded in your context. Connected to your tools. Clear about their limits.",
    "cta": "Design your agent",
    "capabilities": [
      [
        "Business knowledge",
        "Relevant documents, records, and rules. The context to begin in the right place."
      ],
      [
        "Tools & actions",
        "Connections that turn a conversation into a useful next step\u2014with defined permissions."
      ],
      [
        "Human handoff",
        "A route to the right person when context is missing, risk is high, or judgment matters."
      ],
      [
        "Evaluation",
        "Representative workflows, difficult questions, and checks that make improvement visible."
      ]
    ]
  },
  "/managed-agents": {
    "path": "/managed-agents",
    "type": "managed",
    "name": "Managed Agents",
    "category": "Managed agents",
    "headline": "Someone builds it. We stay with it.",
    "description": "A long-term engineering relationship for the agents your business puts to work. Launch, operation, evaluation, and improvement\u2014with clear human ownership.",
    "cta": "Plan the partnership",
    "engagements": [
      [
        "Launch + care",
        "Take the first workflow into operation with an evaluation set, handoff documentation, and an agreed improvement backlog."
      ],
      [
        "Your managed-agent team",
        "Keep engineering, business context, monitoring reviews, and iterative development connected beyond the initial project."
      ],
      [
        "A connected agent estate",
        "Coordinate standards, ownership, access reviews, and release practices across multiple workflows and systems."
      ]
    ],
    "capabilities": [
      "An agreed evaluation set that grows with real-world failures and new requirements.",
      "A review rhythm for task outcomes, exceptions, handoffs, usage, and operating cost.",
      "Controlled changes to knowledge, prompts, integrations, and actions\u2014with regression testing.",
      "Named human owners and a clear route to pause, recover, or roll back a workflow."
    ],
    "engagementIntro": "Define the engagement around your workflows, your systems, and the level of ongoing care you need. No one-size-fits-all promise.",
    "terms": "Support hours, response targets, monitoring scope, security responsibilities, and service levels are agreed per engagement. This concept does not imply a contractual SLA."
  },
  "/implementation-guide": {
    "type": "guide",
    "name": "Choosing an Implementation Partner",
    "headline": "The best partner for your business should be able to show the work.",
    "category": "Buyer guide",
    "proposedOffering": true,
    "path": "/implementation-guide",
    "description": "Choosing a Salesforce, AI, or agentic enterprise implementation partner starts with the work you need done. Use these questions to compare proposals\u2014including ours.",
    "cta": "Discuss your use case",
    "criteria": [
      [
        "Relevant evidence, not a list of logos",
        "Read a complete customer story. Ask what the team actually delivered, which systems were involved, who owned the work, and whether a reference conversation is possible. A Salesforce success does not automatically establish experience with every AI provider."
      ],
      [
        "An explainable architecture",
        "The proposed architecture should identify the system of record, data flows, permissions, integration responsibilities, and where deterministic automation is preferable to an agent. Complexity is not a credential."
      ],
      [
        "A testable definition of success",
        "Agree on representative requests and exceptions, acceptable outputs, required human approvals, and what should cause the workflow to stop. Ask to see how tests are rerun after a change."
      ],
      [
        "Operational ownership",
        "Clarify who monitors failures, handles incidents, refreshes knowledge, approves releases, and manages access. Define what is included in the ongoing engagement and what requires a new scope."
      ],
      [
        "Commercial clarity",
        "Compare the implementation work, provider licenses, usage charges, third-party costs, support scope, and change process separately. A low build estimate is not the same as a predictable operating cost."
      ],
      [
        "A fit with your people",
        "Your team should understand the workflow and the decisions behind it. Look for a partner who makes the system easier to own, not one who makes every change dependent on an unexplained black box."
      ]
    ],
    "sections": [
      [
        "Choosing a platform is a separate decision.",
        "Agentforce, an OpenAI-based application, and a Claude-based workflow are not interchangeable specifications. Compare the task, existing systems, data requirements, controls, integration effort, and results from a representative evaluation. Avoid promises that any one provider is best for every business."
      ],
      [
        "Start with a brief you can use.",
        "Describe the workflow, the users, the systems, the information available, and the outcome you want to improve. Ask the proposed team to respond with assumptions, exclusions, delivery responsibilities, and evidence of relevant experience."
      ]
    ],
    "note": "This is Antrazal\u2019s buyer guidance, not an independent ranking or a guarantee of outcomes. Evaluate the evidence and agreed scope for your own project.",
    "relatedIntent": [
      "/services/salesforce-implementation",
      "/services/ai-implementation",
      "/services/agentic-enterprise-implementation",
      "/services/openai-implementation",
      "/services/claude-implementation"
    ]
  },
  "/services/ai-implementation": {
    "type": "service",
    "name": "AI Implementation",
    "category": "AI",
    "headline": "From a promising use case to a useful business workflow.",
    "scope": "AI implementation is the work of connecting a model to the information, tools, people, and checks a business process needs. Start with a clearly bounded workflow, agree what success means, and test the full process before expanding it. Antrazal brings AI discovery, enterprise integration, and ongoing engineering into one conversation.",
    "capabilities": [
      "Discovery that identifies the user, business outcome, source systems, data permissions, exceptions, and human owner of the workflow.",
      "Knowledge-grounded assistance connected to approved business content, with a clear response when the required evidence is missing.",
      "Scoped tool and API integrations, with validation and explicit approval boundaries for actions that change business records.",
      "Representative evaluations covering expected requests, difficult inputs, misleading content, failed tools, and appropriate human handoffs.",
      "A deployment and improvement plan covering releases, monitoring responsibility, operating cost, rollback, and changes to knowledge or models."
    ],
    "deliverables": [
      [
        "A workflow specification",
        "The trigger, inputs, permitted actions, completion criteria, and exception paths, agreed with the people who own the work."
      ],
      [
        "A tested implementation",
        "A working flow, integration contracts, and an evaluation set that can be rerun when the system changes."
      ],
      [
        "An operating handoff",
        "Runbooks, named responsibilities, release notes, and an improvement backlog rather than a demonstration without ownership."
      ]
    ],
    "questions": [
      [
        "What is the difference between an AI prototype and implementation?",
        "A prototype explores an idea. Implementation also addresses business data, permissions, connected tools, error handling, evaluation, rollout, and day-to-day ownership. The scope should make each responsibility explicit."
      ],
      [
        "Where should an AI implementation begin?",
        "Choose a recurring workflow with an identifiable user, accessible source data, and a result that can be checked. A narrow, measurable first release is easier to evaluate than an open-ended promise to automate everything."
      ],
      [
        "What determines the scope and cost?",
        "The number of systems, data readiness, action permissions, evaluation requirements, channels, and support responsibilities. Those inputs belong in discovery; this website does not imply a fixed price or delivery time."
      ]
    ],
    "relatedIntent": [
      "/services/agentic-enterprise-implementation",
      "/services/openai-implementation",
      "/services/claude-implementation"
    ],
    "proposedOffering": true,
    "path": "/services/ai-implementation",
    "description": "From a promising use case to a useful business workflow.",
    "cta": "Explore your use case"
  },
  "/services/agentic-enterprise-implementation": {
    "type": "service",
    "name": "Agentic Enterprise Implementation",
    "category": "AI",
    "headline": "Connected work. Explicit controls. Human ownership.",
    "scope": "An agentic enterprise workflow can use context and approved tools to coordinate a sequence of business tasks. The design must also explain what it is not allowed to do. Antrazal\u2019s proposed engagement brings workflow engineering, Salesforce integration, evaluation, and managed operation together around that boundary.",
    "capabilities": [
      "Map each task to the systems of record, decision owner, approved tools, and evidence needed to take the next step.",
      "Separate read-only assistance, suggested changes, approved execution, and actions that must always stay with a person.",
      "Design orchestration with bounded task scope, failure handling, retry rules, and duplicate-action prevention appropriate to the connected systems.",
      "Connect Salesforce and other enterprise applications through defined interfaces while preserving the underlying access controls.",
      "Plan evaluation, logs, escalation, cost limits, and release responsibilities before adding more workflows or additional agents."
    ],
    "deliverables": [
      [
        "An authority map",
        "A written boundary for each agent: information it may read, tools it may use, and approvals it must request."
      ],
      [
        "A connected workflow",
        "A scoped flow with tested integration contracts and clear handling of missing data or unavailable systems."
      ],
      [
        "An operating model",
        "Ownership, evaluation, review cadence, and procedures to pause or recover a workflow."
      ]
    ],
    "questions": [
      [
        "Does agentic mean fully autonomous?",
        "No. Autonomy should be scoped to the task and the risk. A workflow can include human approvals, read-only stages, or mandatory escalation. More autonomy is not automatically a better outcome."
      ],
      [
        "Do we need multiple agents?",
        "Not necessarily. Begin with the simplest design that meets the requirement. Add separate agents only when their responsibilities and coordination can be explained, tested, and operated."
      ],
      [
        "How is this different from ordinary automation?",
        "A deterministic process is often the right choice for stable rules. Agentic behavior can help with variable context and tool selection; the implementation still needs deterministic checks around permissions, actions, and completion."
      ]
    ],
    "relatedIntent": [
      "/services/salesforce-agentforce",
      "/services/ai-implementation",
      "/managed-agents"
    ],
    "proposedOffering": true,
    "path": "/services/agentic-enterprise-implementation",
    "description": "Connected work. Explicit controls. Human ownership.",
    "cta": "Explore your use case"
  },
  "/services/openai-implementation": {
    "type": "service",
    "name": "OpenAI Implementation",
    "category": "AI",
    "headline": "OpenAI, connected to the work your business needs done.",
    "scope": "Explore an OpenAI-based workflow with Antrazal: a focused use case, an appropriate model, approved knowledge, and tools that fit the business process. The engagement begins by checking the available data, account configuration, integration requirements, and operating boundaries\u2014not by assuming that a chat interface is the whole solution.",
    "capabilities": [
      "Define the workflow and evaluation cases before choosing the model and the surrounding application architecture.",
      "Design API-connected assistants for supported tasks such as knowledge assistance, document handling, or preparation of service and operations work.",
      "Connect approved business tools with clear input validation, permissions, and confirmation points before changing records.",
      "Review model output and tool behavior against examples that represent the business, including incomplete evidence and failure conditions.",
      "Keep credentials server-side and plan configuration, usage monitoring, release testing, and an agreed human escalation route."
    ],
    "deliverables": [
      [
        "A scoped OpenAI integration",
        "The model configuration, application flow, system connections, and boundaries appropriate to the selected use case."
      ],
      [
        "A repeatable evaluation set",
        "Examples for useful responses, valid actions, missing context, and requests that should be escalated rather than completed."
      ],
      [
        "A handoff your team can use",
        "Integration documentation, configuration ownership, and a plan for reviewing changes after launch."
      ]
    ],
    "questions": [
      [
        "Can OpenAI be connected to Salesforce?",
        "The proposed scope can include an application or service that uses approved Salesforce interfaces. The right design depends on your Salesforce permissions, intended actions, data policy, and available platform capabilities."
      ],
      [
        "Is Antrazal an official OpenAI partner?",
        "This page describes an implementation offering. It does not claim official OpenAI partnership, certification, endorsement, or a particular number of completed OpenAI deployments."
      ],
      [
        "How will business data be handled?",
        "Data flows, access, retention requirements, and provider settings must be reviewed for the chosen deployment. Do not assume that every plan, product, or configuration has the same terms or controls."
      ]
    ],
    "relatedIntent": [
      "/services/ai-implementation",
      "/services/salesforce-implementation",
      "/services/claude-implementation"
    ],
    "proposedOffering": true,
    "path": "/services/openai-implementation",
    "description": "OpenAI, connected to the work your business needs done.",
    "cta": "Explore your use case"
  },
  "/services/claude-implementation": {
    "type": "service",
    "name": "Claude Implementation",
    "category": "AI",
    "headline": "Claude workflows. Designed around enterprise context.",
    "scope": "Explore a Claude-based application or workflow with Antrazal. Begin with the documents, business tasks, and approved tools that matter to your team, then define how outputs will be evaluated and where a human remains in control. Confirm the provider setup and delivery scope during discovery.",
    "capabilities": [
      "Define a bounded document, knowledge, service, or operations workflow and the evidence needed to evaluate its outputs.",
      "Design the application\u2019s context and retrieval approach around approved information, source traceability, and explicit handling of gaps.",
      "Scope Claude API and business-tool integration with validation, access boundaries, and a human handoff for consequential actions.",
      "Test instruction handling, untrusted document content, tool failures, and responses that should ask for clarification or stop.",
      "Plan configuration ownership, usage visibility, change reviews, and ongoing evaluation as business knowledge and model behavior evolve."
    ],
    "deliverables": [
      [
        "A context design",
        "The information sources, selection rules, access assumptions, and output format for the chosen workflow."
      ],
      [
        "A controlled integration",
        "A defined path from the application to approved business tools, with clear action and escalation boundaries."
      ],
      [
        "An evaluation and care plan",
        "Tests, documentation, and operational responsibilities that remain useful after the first release."
      ]
    ],
    "questions": [
      [
        "Is this a Claude subscription or an implementation service?",
        "It is a proposed implementation engagement. Provider accounts, licenses, API access, and commercial terms are assessed separately for the selected architecture."
      ],
      [
        "Is Antrazal an official Anthropic or Claude partner?",
        "No official affiliation, certification, or endorsement is claimed on this page. The proposed service scope and the evidence of relevant delivery experience should be discussed directly."
      ],
      [
        "How do we choose between Claude, OpenAI, and Agentforce?",
        "Compare them against your actual tasks, data environment, integration needs, permissions, evaluation results, and operating requirements. Start with the workflow rather than a universal claim that one platform is best."
      ]
    ],
    "relatedIntent": [
      "/services/openai-implementation",
      "/services/agentic-enterprise-implementation",
      "/managed-agents"
    ],
    "proposedOffering": true,
    "path": "/services/claude-implementation",
    "description": "Claude workflows. Designed around enterprise context.",
    "cta": "Explore your use case"
  }
};

export const voicesSolutionsMetadata = Object.values(voicesSolutionsData).map(({ path, name, description }) => ({
  path,
  title: name,
  description,
}));

export const solutionsProcess = [
  [
    "Understand",
    "Start with the people, the workflow, and what a better result should look like."
  ],
  [
    "Design",
    "Define the knowledge, connections, actions, and boundaries around that work."
  ],
  [
    "Build & test",
    "Make it useful. Test the real scenarios, the edge cases, and the handoffs."
  ],
  [
    "Evolve",
    "Move into operation with ownership, evaluation, and a plan for what improves next."
  ]
];

export const managedCare = {
  title: "Built is a moment. Better is ongoing.",
  description: "Your business moves. Models change. We stay close to the work\u2014evaluating, monitoring, and refining the agents you rely on.",
  items: [
  [
    "Evaluate.",
    "Check the work against real business scenarios."
  ],
  [
    "Monitor.",
    "Review outcomes, exceptions, and human handoffs."
  ],
  [
    "Improve.",
    "Refine the system as your business changes."
  ]
],
};

// Keep the planning choices, not the source demo's simulated approval or success states.
export const agentWorkflows = [
  {
    "name": "Service",
    "request": "Prepare the next step for a customer support case.",
    "steps": [
      "Find approved case context and knowledge",
      "Prepare a grounded response draft",
      "Ask a person to approve the next action"
    ]
  },
  {
    "name": "Revenue",
    "request": "Prepare an account brief before a renewal conversation.",
    "steps": [
      "Gather the approved account context",
      "Draft a renewal conversation brief",
      "Ask the account owner to review"
    ]
  },
  {
    "name": "Operations",
    "request": "Prepare a handoff when an operational exception appears.",
    "steps": [
      "Read the permitted exception context",
      "Prepare a handoff with suggested next steps",
      "Ask the workflow owner to approve"
    ]
  }
];

export const aiPracticeLinks = [
  [
    "/services/chat-agents",
    "Chat Agents"
  ],
  [
    "/services/voice-agents",
    "Voice Agents"
  ],
  [
    "/services/sales-agents-sentiment",
    "Sales Agents (Sentiment)"
  ],
  [
    "/services/ai-service-agents",
    "AI Service Agents"
  ],
  [
    "/services/ai-implementation",
    "AI Implementation"
  ],
  [
    "/services/agentic-enterprise-implementation",
    "Agentic Enterprise Implementation"
  ],
  [
    "/services/openai-implementation",
    "OpenAI Implementation"
  ],
  [
    "/services/claude-implementation",
    "Claude Implementation"
  ]
];

export const solutionLinkNames = {
  "/services/salesforce-agentforce": "Salesforce Agentforce",
  "/services/salesforce-implementation": "Salesforce Implementation"
};

export const solutionEvidence = [
  {
    "path": "/customer-stories/24-hour-home-care-workforce-modernization",
    "name": "24 Hour Home Care",
    "description": "Caregiver experience and platform modernization",
    "image": "/assets/voices/24-hour-home-care.webp"
  },
  {
    "path": "/customer-stories/trivago-support-portal",
    "name": "Trivago",
    "description": "Multilingual Salesforce self-service",
    "image": "/assets/voices/trivago.webp"
  }
];

